import { useEffect, useState } from 'react';

import { getLevelPlayer } from 'services/levels.service';
import { getPublicProfile } from 'services/profile.service';

export interface PerfilNivel {
  nivel?: string;
  level?: string;
  grupo?: string;
  grupoTotal?: number;
  [campo: string]: unknown;
}

interface UsePerfilNivelParams {
  chave?: string;
  cycleId?: string;
  /** `userInfo.grupo`: usado quando o perfil público do ciclo não traz o grupo. */
  grupoReserva?: { nome?: string; total?: number };
}

/**
 * Nível e grupo do participante no ciclo. O perfil público do ciclo tem
 * precedência; sem ele, vale o nível corrente do jogador e o grupo do userInfo.
 * Mesma regra que o Dashboard aplicava inline, agora compartilhada com o Perfil.
 *
 * O resultado fica preso ao pedido (chave + ciclo) que o gerou: trocar de ciclo
 * volta ao carregamento em vez de exibir o nível do ciclo anterior (PERFIL-50).
 * Sem chave ou sem ciclo não há o que carregar, e `carregando` fica falso.
 */
export function usePerfilNivel({ chave, cycleId, grupoReserva }: UsePerfilNivelParams) {
  const pedido = chave && cycleId ? `${chave}|${cycleId}` : undefined;
  const [resultado, setResultado] = useState<{ pedido: string; perfil?: PerfilNivel }>();

  useEffect(() => {
    if (!chave || !cycleId || !pedido) return;

    let ativo = true;

    Promise.all([getLevelPlayer(), getPublicProfile(chave, cycleId)])
      .then(([nivelJogador, perfilPublico]) => {
        const nivel = perfilPublico?.level || nivelJogador?.nivel;

        return {
          ...nivelJogador,
          nivel,
          level: nivel,
          grupo: perfilPublico?.grupo || grupoReserva?.nome,
          grupoTotal: perfilPublico?.grupo_total || grupoReserva?.total,
        } as PerfilNivel;
      })
      .catch(() => undefined)
      .then(perfil => {
        if (ativo) setResultado({ pedido, perfil });
      });

    return () => {
      ativo = false;
    };
    // grupoReserva é objeto do userInfo: a chave já identifica o jogador.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pedido]);

  const atual = resultado?.pedido === pedido ? resultado : undefined;

  return { perfil: atual?.perfil, carregando: !!pedido && !atual };
}
